<script setup>
import { inject, ref, onMounted, computed } from 'vue'
import { getVotingTrend } from '@/api/article.js'
import dayjs from 'dayjs'

const props = defineProps({
  votingId: { type: [String, Number], default: '' },
  show: { type: Boolean, default: true }
})

const size = inject('size')
const extraInfo = inject('extraInfo')
const isMobile = computed(() => ['s', 'xs'].includes(size.value))
const toDateTime = (time) => {
  if (time && dayjs(time).isValid()) {
    return dayjs.unix(time).format('YYYY-MM-DD HH:mm')
  } else {
    return ''
  }
}

const updateTime = ref()
const trendData = ref([])
const isEmpty = computed(() => trendData.value.length === 0)
const loading = ref(true)
const tloading = ref(false)
const fetchVotinTrend = async () => {
  try {
    const res = await getVotingTrend({
      id: extraInfo.value.id,
      event_id: extraInfo.value?.voting_data?.event_id,
      voting_id: props.votingId
    })
    if (res.code !== 0) throw new Error(res.message)
    // 别问我为什么，UI要求的
    if (tloading.value) {
      setTimeout(() => {
        tloading.value = false
        updateTime.value = res?.data?.time
        trendData.value = res?.data?.trend_data
      }, 1500)
    } else {
      updateTime.value = res?.data?.time
      trendData.value = res?.data?.trend_data
    }
    loading.value = false
  } catch (error) {
    console.warn(error)
    if (tloading.value) setTimeout(() => (tloading.value = false), 1500)
    loading.value = false
  }
}
const updateVotinTrend = () => {
  if (tloading.value) return
  tloading.value = true
  fetchVotinTrend()
}

onMounted(() => {
  if (!props.show) {
    loading.value = false
    return
  }
  fetchVotinTrend()
})
</script>

<template>
  <div class="tvb-vote-trend" :class="'trend-' + size">
    <div v-if="isEmpty || !show" class="tvb-vote-trend_empty">
      <template v-if="!loading">
        <tvb-svg-icon class="trend-empty-icon" name="img_statistics" />
        <p>暫時未有投票走勢</p>
      </template>
    </div>
    <template v-else>
      <div class="tvb-vote-trend_header">
        <span>更新時間:&nbsp;{{ toDateTime(updateTime) }}</span>
        <span class="trend-update" :class="{ 'update-loading': tloading }" @click="updateVotinTrend"
          ><tvb-svg-icon class="trend-update-icon" name="icon_epg_refresh" />按此更新</span
        >
      </div>

      <div class="tvb-vote-trend_list">
        <div
          v-for="(item, index) in trendData"
          :key="index"
          class="tvb-vote-trend_item tvb-hairline--surround"
        >
          <div class="tvb-v-trend_item-name">
            <label class="tvb-v-trend_item-tit" :title="item.option_name_tc">{{
              item.option_name_tc
            }}</label>
            <span>{{ item.vote_count_percent }}%</span>
          </div>
          <n-progress
            type="line"
            :percentage="item.vote_count_percent - 0"
            :show-indicator="false"
            :height="isMobile ? 6 : 10"
            :border-radius="8"
            :color="'rgba(255,255,255,0.7)'"
            :rail-color="'rgba(255,255,255,0.1)'"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.tvb-vote-trend {
  .tvb-vote-trend_header {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.8);
    line-height: 22px;
    letter-spacing: 0.4px;
    margin-bottom: 24px;
    display: flex;

    .trend-update {
      display: inline-flex;
      align-items: center;
      margin-left: 12px;
      transition: color 0.15s linear;
      cursor: pointer;
      .trend-update-icon {
        font-size: 16px;
        opacity: 0.7;
        vertical-align: -0.15em;
        overflow: hidden;
        margin-right: 3px;
        transition: opacity 0.15s linear;
      }
      &:not(.update-loading):hover {
        color: #fff;
        .trend-update-icon {
          opacity: 1;
        }
      }

      &.update-loading {
        color: #fff;
        cursor: auto;
        .trend-update-icon {
          opacity: 1;
          animation: tvb-rotate 1s linear infinite;
        }
      }
    }
  }

  .tvb-vote-trend_item {
    & + .tvb-vote-trend_item {
      margin-top: 16px;
    }
    box-sizing: border-box;
    padding: 14px 16px 18px;
    background-color: var(--tvb-vote-bgc);
    border-radius: 10px;
    --tvb-hairline-border-radius: calc(2 * 10px);
    --tvb-hairline-border-color: var(--tvb-vote-border-color);

    .tvb-v-trend_item-name {
      font-size: 15px;
      color: rgba(255, 255, 255, 0.9);
      line-height: 22px;
      margin-bottom: 10px;
      letter-spacing: 0.4px;
      display: flex;
      justify-content: space-between;
    }
    .tvb-v-trend_item-tit {
      @include line-text-ellipsis(1);
    }
  }

  .tvb-vote-trend_empty {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-size: 16px;
    color: rgba(255, 255, 255, 0.8);
    line-height: 24px;
    letter-spacing: 0.5px;
    height: 568px - 24px - 32px;
    .trend-empty-icon {
      font-size: 120px;
      vertical-align: -0.15em;
      overflow: hidden;
      margin-bottom: 24px;
      margin-top: 32px - 24px;
    }
  }

  &.trend-s,
  &.trend-xs {
    .tvb-vote-trend_header {
      font-size: 13px;
      line-height: 18px;
      margin-bottom: 16px;
      .trend-update .trend-update-icon {
        margin-right: 2px;
      }
    }
    .tvb-vote-trend_item {
      & + .tvb-vote-trend_item {
        margin-top: 8px;
      }
      padding: 8px 10px 12px;
      border-radius: 8px;
      --tvb-hairline-border-radius: calc(2 * 8px);
      .tvb-v-trend_item-name {
        font-size: 13px;
        line-height: 18px;
        letter-spacing: 0;
        margin-bottom: 8px;
      }
    }

    .tvb-vote-trend_empty {
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.3px;
      height: 366px - 12px * 2;
      .trend-empty-icon {
        font-size: 90px;
        margin-bottom: 16px;
      }
    }
  }
}
</style>
